import classNames from "classnames"
import React, { useEffect, useRef } from "react"

export function PageHeader({
  children,
  actions,
  navigation,
  className,
  inactive = false,
  profile = false,
}: {
  /**
   * Actions to be displayed on the right side of the header. Elements
   * should be styled as buttons.
   */
  actions?: React.ReactNode
  /**
   * The main content of the page header. It will be displayed prominently. For simple pages, providing a h1 will suffice.
   */
  children: React.ReactNode
  className?: string
  /**
   * Indicates whether the page header is inactive or not. When set to true, the page header is visually inactive
   */
  inactive?: boolean
  /**
   * Navigation links to be displayed on the left side of the header.
   * Contents should be an unordered list with items being links or
   * buttons. Specify `selected` for the active link (as well as adding
   * `aria-current="page"` for accessibility).
   */
  navigation?: React.ReactNode
  /**
   * Indicates whether the page header is a profile page header or not. When set to true, the page header will visually center the title and actions at mobile breakpoints.
   */
  profile?: boolean
}) {
  const hasMultiActions = actions
    ? React.isValidElement(actions) && actions.type === React.Fragment
      ? React.Children.count(actions.props.children) > 1
      : React.Children.count(actions) > 1
    : false

  const nav = useScrollToSelected(navigation)

  return (
    <header
      className={classNames("tds-page-header", className, {
        "has-multi-actions": hasMultiActions,
        "has-nav": !!navigation,
        inactive,
        "tds-page-header--profile": profile,
      })}
    >
      <div className="tds-page-header__title-bar">
        <div className="tds-page-header__primary">{children}</div>
        {actions && (
          <div className="tds-page-header__actions tds-no-print">{actions}</div>
        )}
      </div>
      {navigation && (
        <nav ref={nav} className="tds-page-header__nav tds-no-print">
          {navigation}
        </nav>
      )}
    </header>
  )
}

function useScrollToSelected(navigation: React.ReactNode) {
  const nav = useRef<HTMLElement>(null)
  useEffect(() => {
    if (!nav.current) return
    const ulElement = nav.current.querySelector("ul")
    if (!ulElement) return
    const selectedItem = ulElement.querySelector(".selected")
    if (selectedItem) {
      const bounds = selectedItem.getBoundingClientRect()
      const ulBounds = ulElement.getBoundingClientRect()
      if (bounds.right > ulBounds.right || bounds.left < ulBounds.left) {
        ulElement.scrollTo({
          left: ulElement.scrollLeft + bounds.left - ulBounds.left,
        })
      }
    }
  }, [navigation])
  return nav
}
